import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { organizationsApi, projectsApi } from "@/lib/api";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import SendToCompassButton from "@/components/compass/SendToCompassButton";
import {
  ArrowLeft, Building2, ExternalLink, Users, Loader2,
  StickyNote, History, MapPin, Route, FileText, CheckSquare,
  Trash2, Save, Plus, X,
} from "lucide-react";

const REL_STATUSES = ["Researching","Potential Fit","Outreach","Partner","Not Pursuing"];
const ORG_STATUSES = ["Active","Inactive","Unknown"];

const REL_COLOR = {
  "Researching": "bg-blue-500/15 text-blue-300 border-blue-500/25",
  "Potential Fit": "bg-amber-500/15 text-amber-300 border-amber-500/25",
  "Outreach": "bg-violet-500/15 text-violet-300 border-violet-500/25",
  "Partner": "bg-emerald-500/25 text-emerald-200 border-emerald-500/40",
  "Not Pursuing": "bg-white/5 text-white/50 border-white/15",
};
const ORG_STATUS_COLOR = {
  "Active": "bg-emerald-500/15 text-emerald-300 border-emerald-500/25",
  "Inactive": "bg-white/5 text-white/50 border-white/15",
  "Unknown": "bg-white/5 text-white/40 border-white/10",
};

function fmtDate(s) {
  if (!s) return "";
  try {
    const d = new Date(s);
    return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch { return s; }
}

function fmtDateTime(s) {
  if (!s) return "";
  try {
    const d = new Date(s);
    return d.toLocaleString(undefined, {
      year: "numeric", month: "short", day: "numeric",
      hour: "numeric", minute: "2-digit",
    });
  } catch { return s; }
}

/**
 * Render one audit_logs row as a human-readable history line. Uses
 * only meta the backend actually attaches — no inventing missing
 * before/after data. Falls back to the action + date + actor when the
 * audit row is older and lacks structured meta.
 */
function describeHistory(h) {
  const m = h.meta || {};
  switch (h.action) {
    case "organization_created":
      return { title: "Organization created", detail: m.name || null };
    case "organization_updated": {
      const keys = Object.keys(m).filter(k => k !== "updated_at");
      const label = keys.length === 1
        ? `Organization ${keys[0].replaceAll("_", " ")} updated`
        : keys.length > 1
          ? `Organization updated (${keys.length} fields)`
          : "Organization updated";
      return { title: label, detail: null };
    }
    case "relationship_added":
      return {
        title: `Relationship added: ${m.entity_name || m.entity_id || "entity"}`,
        detail: m.status ? `Status · ${m.status}` : null,
      };
    case "relationship_updated": {
      const parts = [];
      if (m.previous_relationship_status && m.relationship_status
          && m.previous_relationship_status !== m.relationship_status) {
        parts.push(`Status ${m.previous_relationship_status} → ${m.relationship_status}`);
      } else if (m.relationship_status) {
        parts.push(`Status → ${m.relationship_status}`);
      }
      if ("project_id" in m) {
        const prev = m.previous_project_name || (m.previous_project_id ? "linked project" : "No project");
        const next = m.project_name || (m.project_id ? "linked project" : "No project");
        if (prev !== next) parts.push(`Related project ${prev} → ${next}`);
      }
      if (m.why_it_matters !== undefined && parts.length === 0) {
        parts.push("Rationale updated");
      }
      const entLabel = m.entity_name ? ` (${m.entity_name})` : "";
      return {
        title: parts.length
          ? `${parts[0]}${entLabel}`
          : `Relationship updated${entLabel}`,
        detail: parts.slice(1).join(" · ") || null,
      };
    }
    case "relationship_status_migrated":
      return {
        title: `Legacy status migrated: ${m.legacy_active_status || "Active"} → ${m.migrated_to}`,
        detail: null,
      };
    case "org_note_created":
      return {
        title: "Note added",
        detail: m.content_preview ? `"${m.content_preview}"` : null,
      };
    case "org_note_updated":
      return {
        title: "Note edited",
        detail: m.content_preview ? `"${m.content_preview}"` : null,
      };
    case "org_note_deleted":
      return {
        title: "Note deleted",
        detail: m.content_preview ? `"${m.content_preview}"` : null,
      };
    case "contact_linked":
      return { title: "Contact linked", detail: null };
    case "contact_unlinked":
      return { title: "Contact unlinked", detail: null };
    default:
      return { title: h.action.replaceAll("_", " "), detail: null };
  }
}

function HistoryRow({ h }) {
  const { title, detail } = describeHistory(h);
  return (
    <div className="flex items-start justify-between gap-3"
         data-testid={`org-history-row-${h.id}`}>
      <div className="min-w-0">
        <div className="text-white/90 truncate">{title}</div>
        {detail && <div className="text-white/50 mt-0.5 truncate">{detail}</div>}
        <div className="text-white/40 mt-0.5">
          {fmtDateTime(h.created_at)}
          {h.actor_name ? ` · ${h.actor_name}` : ""}
        </div>
      </div>
    </div>
  );
}

export default function OrganizationProfilePage() {
  const { id } = useParams();
  const [org, setOrg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [projectsByEntity, setProjectsByEntity] = useState({});
  const [noteDraft, setNoteDraft] = useState({ content: "", entity_id: "", project_id: "" });
  const [savingNote, setSavingNote] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await organizationsApi.get(id);
      setOrg(r.data);
      // Preload projects for every entity that has a relationship so
      // the per-row project selector doesn't need an extra fetch.
      const ents = new Set((r.data.relationships || []).map(x => x.entity_id));
      const map = {};
      await Promise.all(Array.from(ents).map(async eid => {
        try { map[eid] = (await projectsApi.list(eid)).data || []; }
        catch { map[eid] = []; }
      }));
      setProjectsByEntity(map);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load organization");
    } finally { setLoading(false); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const updateStatus = async (relId, status) => {
    try {
      await organizationsApi.updateRelationship(id, relId, { relationship_status: status });
      toast.success("Relationship status updated");
      load();
    } catch { toast.error("Update failed"); }
  };
  const updateProject = async (relId, pid) => {
    try {
      await organizationsApi.updateRelationship(id, relId, { project_id: pid || null });
      toast.success(pid ? "Project linked" : "Project removed");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
  };
  const updateOrgStatus = async (v) => {
    try {
      await organizationsApi.update(id, { organization_status: v });
      toast.success("Organization status updated");
      load();
    } catch { toast.error("Update failed"); }
  };

  const projectsForEntity = (eid) => (projectsByEntity[eid] || []);

  const createNote = async () => {
    if (!noteDraft.content.trim()) return toast.error("Note content required");
    setSavingNote(true);
    try {
      await organizationsApi.createNote(id, {
        content: noteDraft.content.trim(),
        entity_id: noteDraft.entity_id || null,
        project_id: noteDraft.project_id || null,
      });
      setNoteDraft({ content: "", entity_id: "", project_id: "" });
      toast.success("Note saved");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Note failed");
    } finally { setSavingNote(false); }
  };
  const removeNote = async (nid) => {
    if (!window.confirm("Delete this note?")) return;
    try {
      await organizationsApi.deleteNote(id, nid);
      toast.success("Note deleted");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const noteEntityOptions = useMemo(
    () => (org?.relationships || []).map(r => ({ id: r.entity_id, name: r.entity_name || r.entity_id })),
    [org]);
  const noteProjectOptions = useMemo(
    () => noteDraft.entity_id ? projectsForEntity(noteDraft.entity_id) : [],
    // eslint-disable-next-line
    [noteDraft.entity_id, projectsByEntity]);

  if (loading) return (
    <div className="h-full flex items-center justify-center text-white/40" data-testid="org-loading">
      <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
    </div>
  );
  if (!org) return (
    <div className="h-full flex flex-col items-center justify-center text-white/40 gap-2">
      <p>Organization not found.</p>
      <Link to="/organizations" className="text-white/70 hover:text-white text-sm">← Back to Organizations</Link>
    </div>
  );

  return (
    <div className="h-full overflow-y-auto overflow-x-hidden max-w-full" data-testid="organization-profile">
      <div className="max-w-4xl mx-auto p-6 md:p-10 space-y-6">
        <Link to="/organizations" className="text-white/50 hover:text-white/80 text-sm inline-flex items-center gap-1">
          <ArrowLeft className="w-3.5 h-3.5" /> All organizations
        </Link>

        {/* Header ================================================== */}
        <div className="rounded-xl border border-white/10 bg-white/[0.02] p-6">
          <div className="flex items-start gap-3 flex-wrap">
            <Building2 className="w-6 h-6 text-white/70 mt-1" />
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <h1 className="text-3xl font-display font-semibold tracking-tight" data-testid="org-name">{org.name}</h1>
                <SendToCompassButton targetType="organization" targetId={org.id}
                                      testid="org-send-to-compass" />
              </div>
              <div className="flex items-center gap-2 text-xs text-white/60 mt-2 flex-wrap">
                <Badge variant="outline" className="border-white/15 text-white/70 bg-white/5">{org.organization_type}</Badge>
                <div className="inline-flex items-center gap-1.5"
                     data-testid="org-status-block">
                  <span className="text-[10px] uppercase tracking-wider text-white/40">Organization Status</span>
                  <Select value={org.organization_status || "Unknown"} onValueChange={updateOrgStatus}>
                    <SelectTrigger className="w-[130px] h-7 text-xs bg-white/5 border-white/10" data-testid="org-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#151A23] border-white/10 text-white">
                      {ORG_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="inline-flex items-center gap-1.5"
                     data-testid="org-scope-block">
                  <span className="text-[10px] uppercase tracking-wider text-white/40">Geographic Scope</span>
                  <span className={`text-[11px] px-2 py-0.5 rounded-md border ${ORG_STATUS_COLOR[org.geographic_scope && org.geographic_scope !== "Unknown" ? "Active" : "Unknown"]}`}>
                    {org.geographic_scope || "Unknown"}
                  </span>
                </div>
                {org.website && (
                  <a href={org.website} target="_blank" rel="noopener noreferrer"
                     className="inline-flex items-center gap-1 hover:text-white/85">
                    {org.domain || org.website} <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>
              {(org.home_country || org.city_region || (org.countries_operated || []).length > 0 || (org.languages || []).length > 0) && (
                <div className="text-xs text-white/50 mt-3 flex flex-wrap gap-3">
                  {(org.home_country || org.city_region) && (
                    <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {[org.city_region, org.home_country].filter(Boolean).join(", ")}</span>
                  )}
                  {(org.countries_operated || []).length > 0 && (
                    <span>Operates in · {org.countries_operated.slice(0, 4).join(", ")}{org.countries_operated.length > 4 ? "…" : ""}</span>
                  )}
                  {(org.languages || []).length > 0 && (
                    <span>Languages · {org.languages.join(", ")}</span>
                  )}
                </div>
              )}
              {org.summary && <p className="text-sm text-white/80 mt-4 leading-relaxed">{org.summary}</p>}
            </div>
          </div>
        </div>

        {/* Relevant entities ======================================= */}
        <div>
          <div className="text-eyebrow mb-2">Relevant entities</div>
          {(org.relationships || []).length === 0 ? (
            <div className="text-sm text-white/40">No relationships yet.</div>
          ) : (
            <div className="space-y-3">
              {org.relationships.map(r => {
                const projects = projectsForEntity(r.entity_id);
                return (
                  <div key={r.id} className="p-4 rounded-lg border border-white/10 bg-white/[0.02]"
                       data-testid={`org-rel-${r.id}`}>
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="min-w-0">
                        <div className="text-white font-medium">
                          {r.entity_name || r.entity_id}
                        </div>
                        {r.why_it_matters && (
                          <p className="text-xs text-white/70 mt-1.5 leading-relaxed max-w-2xl">{r.why_it_matters}</p>
                        )}
                        <div className="text-[11px] text-white/40 mt-2 flex flex-wrap gap-2">
                          {r.added_by_name && <span>Added by {r.added_by_name}</span>}
                          {r.added_at && <span>· {fmtDate(r.added_at)}</span>}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <Select value={r.project_id || "__none"}
                                onValueChange={v => updateProject(r.id, v === "__none" ? "" : v)}>
                          <SelectTrigger className="w-[180px] h-8 bg-white/5 border-white/10 text-xs"
                                         data-testid={`org-rel-project-${r.id}`}>
                            <SelectValue placeholder="No project" />
                          </SelectTrigger>
                          <SelectContent className="bg-[#151A23] border-white/10 text-white max-h-64">
                            <SelectItem value="__none">No project</SelectItem>
                            {projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        <Select value={r.relationship_status}
                                onValueChange={v => updateStatus(r.id, v)}>
                          <SelectTrigger className="w-[170px] h-8 bg-white/5 border-white/10 text-xs"
                                         data-testid={`org-rel-status-${r.id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-[#151A23] border-white/10 text-white">
                            {REL_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    {(r.matched_signals || []).length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {r.matched_signals.map((s, i) => (
                          <span key={i} className="text-[11px] px-2 py-0.5 rounded-md bg-white/[0.05] border border-white/10 text-white/70">{s}</span>
                        ))}
                      </div>
                    )}
                    <div className="mt-2 flex items-center gap-2 text-[11px] text-white/40">
                      <span className={`px-2 py-0.5 rounded-md border ${REL_COLOR[r.relationship_status]}`}>
                        {r.relationship_status}
                      </span>
                      {r.confidence > 0 && <span>· {Math.round(r.confidence * 100)}% match</span>}
                      {r.project_name && <span>· {r.project_name}</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Organization notes ======================================= */}
        <div data-testid="org-notes-section">
          <div className="text-eyebrow mb-2 flex items-center gap-1.5">
            <StickyNote className="w-3.5 h-3.5" /> Organization notes
          </div>
          <div className="p-4 rounded-lg border border-white/10 bg-white/[0.02] space-y-2">
            <Textarea rows={2} value={noteDraft.content}
              onChange={e => setNoteDraft(v => ({ ...v, content: e.target.value }))}
              placeholder="Institutional memory — calls, mentions, context…"
              className="bg-white/5 border-white/10 text-sm"
              data-testid="org-note-input" />
            <div className="flex items-center gap-2 flex-wrap">
              <Select value={noteDraft.entity_id || "__none"}
                onValueChange={v => setNoteDraft(vv => ({ ...vv, entity_id: v === "__none" ? "" : v, project_id: "" }))}>
                <SelectTrigger className="w-[180px] h-8 bg-white/5 border-white/10 text-xs"
                               data-testid="org-note-entity">
                  <SelectValue placeholder="Any entity" />
                </SelectTrigger>
                <SelectContent className="bg-[#151A23] border-white/10 text-white max-h-64">
                  <SelectItem value="__none">Any entity</SelectItem>
                  {noteEntityOptions.map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
                </SelectContent>
              </Select>
              {noteDraft.entity_id && (
                <Select value={noteDraft.project_id || "__none"}
                  onValueChange={v => setNoteDraft(vv => ({ ...vv, project_id: v === "__none" ? "" : v }))}>
                  <SelectTrigger className="w-[180px] h-8 bg-white/5 border-white/10 text-xs"
                                 data-testid="org-note-project">
                    <SelectValue placeholder="No project" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#151A23] border-white/10 text-white max-h-64">
                    <SelectItem value="__none">No project</SelectItem>
                    {noteProjectOptions.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
              <div className="flex-1" />
              <Button size="sm" onClick={createNote} disabled={savingNote || !noteDraft.content.trim()}
                className="rounded-full bg-white text-black hover:bg-white/90"
                data-testid="org-note-save">
                <Plus className="w-3.5 h-3.5 mr-1" /> {savingNote ? "Saving…" : "Add note"}
              </Button>
            </div>
          </div>
          <div className="space-y-2 mt-3">
            {(org.organization_notes || []).map(n => (
              <div key={n.id} className="p-3 rounded-lg border border-white/10 bg-white/[0.02]"
                   data-testid={`org-note-${n.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <p className="text-sm text-white whitespace-pre-wrap leading-relaxed">{n.content}</p>
                  <Button variant="ghost" size="icon" onClick={() => removeNote(n.id)}
                    className="w-7 h-7 text-white/40 hover:text-white/80 hover:bg-white/10 flex-shrink-0"
                    data-testid={`org-note-delete-${n.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
                <div className="text-[11px] text-white/40 mt-2 flex flex-wrap gap-2">
                  <span>{n.author_name || "Author"}</span>
                  <span>· {fmtDate(n.created_at)}</span>
                  {n.entity_id && <span>· Entity linked</span>}
                  {n.project_id && <span>· Project linked</span>}
                </div>
              </div>
            ))}
            {(org.organization_notes || []).length === 0 && (
              <div className="text-xs text-white/40 italic">No organization notes yet.</div>
            )}
          </div>
        </div>

        {/* Related contacts ======================================= */}
        {(org.contacts || []).length > 0 && (
          <div>
            <div className="text-eyebrow mb-2 flex items-center gap-1.5">
              <Users className="w-3.5 h-3.5" /> Related contacts
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {org.contacts.map(c => (
                <Link to={`/contacts/${c.id}`} key={c.id}
                      className="p-3 rounded-lg border border-white/10 bg-white/[0.02] hover:border-white/25 transition-colors block">
                  <div className="text-white text-sm">{c.full_name}</div>
                  <div className="text-xs text-white/50">{c.role || c.email || ""}</div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Related notes + tasks (intake-linked) ================== */}
        {(org.notes || []).length > 0 && (
          <div>
            <div className="text-eyebrow mb-2 flex items-center gap-1.5">
              <FileText className="w-3.5 h-3.5" /> Related notes (Intake)
            </div>
            <div className="space-y-2">
              {org.notes.map(n => (
                <div key={n.id} className="p-3 rounded-lg border border-white/10 bg-white/[0.02]">
                  <div className="text-sm text-white">{n.title}</div>
                  <p className="text-xs text-white/60 mt-1 line-clamp-2">{n.content}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {(org.tasks || []).length > 0 && (
          <div>
            <div className="text-eyebrow mb-2 flex items-center gap-1.5">
              <CheckSquare className="w-3.5 h-3.5" /> Related tasks
            </div>
            <div className="space-y-2">
              {org.tasks.map(t => (
                <div key={t.id} className="p-3 rounded-lg border border-white/10 bg-white/[0.02]">
                  <div className="text-sm text-white">{t.name}</div>
                  <div className="text-xs text-white/50 mt-1">{t.status} · {t.priority}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Provenance breadcrumb ================================== */}
        {(org.provenance?.steps || []).length > 0 && (
          <div data-testid="org-provenance">
            <div className="text-eyebrow mb-2 flex items-center gap-1.5">
              <Route className="w-3.5 h-3.5" /> Provenance
            </div>
            <div className="p-4 rounded-lg border border-white/10 bg-white/[0.02] text-xs text-white/70 leading-relaxed">
              {org.provenance.steps.map((s, i) => (
                <span key={i}>
                  {i > 0 && <span className="text-white/30"> → </span>}
                  {s.step === "source" ? (
                    <a href={s.url} target="_blank" rel="noopener noreferrer"
                       className="text-white/80 hover:text-white inline-flex items-center gap-1">
                      Source <ExternalLink className="w-3 h-3" />
                    </a>
                  ) : s.step === "intake" ? (
                    <span className="text-white/80">Intake</span>
                  ) : s.step === "organization" ? (
                    <span className="text-white/85">Organization · {fmtDate(s.created_at)}{s.created_by_name ? ` · ${s.created_by_name}` : ""}</span>
                  ) : null}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* History (audit) ======================================== */}
        {(org.history || []).length > 0 && (
          <div data-testid="org-history">
            <div className="text-eyebrow mb-2 flex items-center gap-1.5">
              <History className="w-3.5 h-3.5" /> History
            </div>
            <div className="p-4 rounded-lg border border-white/10 bg-white/[0.02] space-y-2 text-xs">
              {org.history.slice(0, 20).map(h => (
                <HistoryRow key={h.id} h={h} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
